import { useEffect, useMemo, useState } from "react";
import { hexToRgba } from "../../../utilities";
import {
    AvailableVolumeTemplates,
    buildMetafieldFromSwatchOptions,
    getPriceLabel,
    parseCTAWithIcon,
    renderBundleErrorMessage,
} from "../../constants";
import { VolumeConfigInterface, ProductObj, VariantsObj } from "../../Types";
import { CustomSwatches, Swatches, VariantSelectorV2 } from "../../BundlePreviewWrapper/BundlePreviewWrapper";
import styles from "./BundlePreviewFooter.module.css";
import Button from "../../BundlesComponents/Button";
import { InspectorEditableBlock } from "../../CommonFields/InspectorEditableBlock";

const FreeGiftItem = ({
    index,
    _pd,
    standAlone,
    bundleConfig,
    updateProductVariants,
    money_format,
    currency,
}: {
    _pd: ProductObj;
    index: number;
    standAlone: boolean;
    targetForm?: any;
    bundleConfig: VolumeConfigInterface;
    setFreeGiftSelectedVariants?: any;
    updateProductVariants?: any;
    currency: any;
    money_format: string;
}) => {
    const [selectedVariants, setSelectedVariants] = useState<Array<VariantsObj>>(
        new Array(_pd.quantity).fill(_pd.variants.find((vv) => vv.availableForSale == true) || _pd.variants[0]),
    );
    const [lastChangedIndex, setLastChangedIndex] = useState(0);

    const handleVariantsChange = (i: number) => (updated: VariantsObj[]) => {
        setLastChangedIndex(i);
        setSelectedVariants(updated);
    };

    useEffect(() => {
        const updatedSelectedVariants = selectedVariants?.map((variant) => ({
            ...variant,
            free_gift: true,
        }));
        _pd.selectedVariants = updatedSelectedVariants;
        updateProductVariants(index, updatedSelectedVariants);
    }, [selectedVariants]);

    useEffect(() => {
        const svs = new Array(_pd.quantity).fill(
            _pd.variants.find((vv) => vv.availableForSale == true) || _pd.variants[0],
        );
        setSelectedVariants(svs);
        setLastChangedIndex(0);
    }, [_pd]);

    const variantImage = selectedVariants[lastChangedIndex]?.image || selectedVariants.find((v) => v?.image)?.image;
    const imageObj = variantImage || _pd.images?.[0];
    const image = imageObj ? imageObj.originalSrc || imageObj.src : "";

    const isProductAvailable = useMemo(() => {
        return _pd.variants.some((vv) => (vv.quantityAvailable && vv.quantityAvailable > 0) || vv.availableForSale);
    }, [_pd]);

    const userShopname = standAlone ? window.kefi_bundles.shop : shopify.config.shop;
    const effectiveMetafield = buildMetafieldFromSwatchOptions({
        product: _pd,
        bundleConfig: bundleConfig,
    });

    const getNewPriceLabel = () => {
        const priceLabel = getPriceLabel(
            selectedVariants?.reduce((total, sv) => total + +sv?.price, 0),
            money_format,
            currency,
        );

        return (
            <div className={styles.fgPriceWrapper}>
                <span className={styles.fgText}>{bundleConfig?.free_text}</span>
                <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="freeGiftCompareAtPrice">
                    <span
                        style={{
                            color:
                                bundleConfig?.bundles_design?.free_gift_compare_price_color ||
                                bundleConfig?.bundles_design?.product_price_color ||
                                "#030303",
                            fontSize:
                                bundleConfig?.bundles_design?.free_gift_compare_price_size ||
                                bundleConfig?.bundles_design.product_price_size,
                            fontWeight: (
                                bundleConfig?.bundles_design?.free_gift_compare_price_style ||
                                bundleConfig?.bundles_design?.product_price_style
                            )?.split(" ")[0],
                            fontStyle: (
                                bundleConfig?.bundles_design?.free_gift_compare_price_style ||
                                bundleConfig?.bundles_design?.product_price_style
                            )?.split(" ")[1],
                        }}
                        className={styles.fgPriceLabel}
                    >
                        {priceLabel}
                    </span>
                </InspectorEditableBlock>
            </div>
        );
    };

    return (
        <>
            {index > 0 && (
                <span
                    className={styles.freeGiftSeparator}
                    style={{
                        background: hexToRgba(bundleConfig?.bundles_design?.free_gift_header_bg_color, 0.3),
                    }}
                ></span>
            )}
            <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="freeGiftProduct">
                <div
                    className={styles.freeGiftContainer}
                    title={_pd.title}
                    style={{ alignItems: _pd.variants.length > 1 ? "flex-start" : "center" }}
                >
                    <div className={styles.freeGiftImgWrapper}>
                        <img src={image} alt="" className={styles.fgImg} />
                        {_pd?.selectedVariants?.length > 1 && (
                            <div className={styles.freeGiftBadge}>x{`${_pd?.selectedVariants?.length}`}</div>
                        )}
                    </div>

                    <div className={styles.freeGiftInfoGrid}>
                        <div className={styles.freeGiftTextCol}>
                            {bundleConfig?.free_gift_template == "modern" ? (
                                <p
                                    className={styles.freeGiftText}
                                    style={{
                                        fontFamily:
                                            bundleConfig.bundles_design?.bundle_widget_font_family ==
                                            "Custom Store Font"
                                                ? "inherit"
                                                : bundleConfig.bundles_design?.bundle_widget_font_family,
                                        color: bundleConfig?.bundles_design?.free_gift_text_color,
                                        fontSize: bundleConfig?.bundles_design.free_gift_text_size,
                                        fontWeight: bundleConfig?.bundles_design?.free_gift_text_style?.split(" ")[0],
                                        fontStyle: bundleConfig?.bundles_design?.free_gift_text_style?.split(" ")[1],
                                    }}
                                >
                                    {bundleConfig?.free_gift_text?.includes("{product_title}")
                                        ? bundleConfig.free_gift_text.replace(/{product_title}/g, _pd?.title || "")
                                        : bundleConfig?.free_gift_text}
                                </p>
                            ) : (
                                <p
                                    className={styles.freeGiftText}
                                    style={{
                                        fontFamily:
                                            bundleConfig.bundles_design?.bundle_widget_font_family ==
                                            "Custom Store Font"
                                                ? "inherit"
                                                : bundleConfig.bundles_design?.bundle_widget_font_family,
                                        color: bundleConfig?.bundles_design?.free_gift_text_color,
                                        fontSize: bundleConfig?.bundles_design.free_gift_text_size,
                                        fontWeight: bundleConfig?.bundles_design?.free_gift_text_style?.split(" ")[0],
                                        fontStyle: bundleConfig?.bundles_design?.free_gift_text_style?.split(" ")[1],
                                    }}
                                >
                                    {_pd.title}
                                </p>
                            )}

                            {!isProductAvailable && <span className={styles.freeGiftOutOfStock}>Out of stock!</span>}

                            {_pd.variants.length > 1 && isProductAvailable && (
                                <div className={styles.freeGiftVariantList}>
                                    {selectedVariants.map((_, i: number) =>
                                        userShopname === "typsy-beauty.myshopify.com" ? (
                                            <Swatches
                                                bundlesDesign={bundleConfig?.bundles_design}
                                                key={i}
                                                variants={_pd.variants}
                                                handleChange={handleVariantsChange(i)}
                                                selected={selectedVariants}
                                                productCardIndex={i}
                                                standAlone={standAlone}
                                                options={_pd.options || []}
                                            />
                                        ) : bundleConfig?.show_swatches && effectiveMetafield ? (
                                            <CustomSwatches
                                                key={i}
                                                variants={_pd.variants}
                                                handleChange={handleVariantsChange(i)}
                                                selected={selectedVariants}
                                                options={_pd.options || []}
                                                productCardIndex={i}
                                                standAlone={standAlone}
                                                metafield={effectiveMetafield}
                                                bundlesDesign={bundleConfig?.bundles_design}
                                                bundleConfig={bundleConfig}
                                                hideLabel={!!bundleConfig?.show_option_labels_once && i !== 0}
                                                hideSerialNumber={!!bundleConfig?.hide_item_numbers}
                                            />
                                        ) : (
                                            <VariantSelectorV2
                                                key={i}
                                                variants={_pd.variants}
                                                handleChange={handleVariantsChange(i)}
                                                selected={selectedVariants}
                                                productCardIndex={i}
                                                standAlone={standAlone}
                                                bundlesDesign={bundleConfig?.bundles_design}
                                                options={_pd.options || []}
                                                metafield={effectiveMetafield}
                                                bundleConfig={bundleConfig}
                                                hideLabel={!!bundleConfig?.show_option_labels_once && i !== 0}
                                                hideSerialNumber={!!bundleConfig?.hide_item_numbers}
                                            />
                                        ),
                                    )}
                                </div>
                            )}
                        </div>

                        {bundleConfig?.free_gift_show_price !== false && (
                            <div
                                className={styles.freeGiftPriceCol}
                                style={{
                                    fontFamily:
                                        bundleConfig.bundles_design?.bundle_widget_font_family == "Custom Store Font"
                                            ? "inherit"
                                            : bundleConfig.bundles_design?.bundle_widget_font_family,
                                    color: bundleConfig?.bundles_design?.free_gift_text_color,
                                    fontSize: bundleConfig?.bundles_design.free_gift_text_size,
                                    fontWeight: bundleConfig?.bundles_design?.free_gift_text_style?.split(" ")[0],
                                    fontStyle: bundleConfig?.bundles_design?.free_gift_text_style?.split(" ")[1],
                                }}
                            >
                                {getNewPriceLabel()}
                            </div>
                        )}
                    </div>
                </div>
            </InspectorEditableBlock>
        </>
    );
};

export const FreeGiftComponent = ({
    products,
    bundleConfig,
    standAlone,
    targetForm,
    setFreeGiftSelectedVariants = null,
    active,
    index,
    money_format,
    currency,
}: {
    products: ProductObj[];
    bundleConfig: VolumeConfigInterface;
    standAlone: boolean;
    targetForm: any;
    setFreeGiftSelectedVariants?: any | null;
    active?: number;
    index?: number;
    money_format: string;
    currency: any;
}) => {
    const [_products, setProducts] = useState<ProductObj[]>([]);

    useEffect(() => {
        setProducts(products);
    }, [products]);
    useEffect(() => {
        if (setFreeGiftSelectedVariants && active == index) {
            setFreeGiftSelectedVariants(() => {
                const temp: VariantsObj[] = [];
                products.forEach((pd: ProductObj) => {
                    temp.push(...pd.selectedVariants);
                });
                return temp;
            });
        }
    }, [_products, index, active]);

    const updateProductVariants = (index: number, selectedVariants: VariantsObj[]) => {
        if (selectedVariants.length) {
            setProducts((ps: ProductObj[]) => {
                const temp: ProductObj[] = [...ps];
                temp[index].selectedVariants = selectedVariants;
                return temp;
            });
        }
    };

    return (
        <div
            style={{
                borderRadius: [
                    AvailableVolumeTemplates.VERTICAL_BASIC,
                    AvailableVolumeTemplates.VERTICAL_MODERN,
                    AvailableVolumeTemplates.VERTICAL_CLASSIC,
                ].includes(bundleConfig?.selected_template_volume)
                    ? undefined
                    : bundleConfig?.bundles_design?.border_radius,
                backgroundColor: active == index ? bundleConfig.bundles_design?.free_gift_bg_color : "transparent",
                paddingBottom: bundleConfig?.free_gift_text?.length ? "8px" : "4px",
                ...(bundleConfig.free_gift_template === "modern" && {
                    borderRadius: "0",
                }),
            }}
            className={styles.freeGiftComponentContainer}
        >
            {bundleConfig?.free_gift_template !== "modern" && !!bundleConfig?.free_gift_text?.length && (
                <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="freeGiftText">
                    <p
                        className={`${styles.freeGiftText} ${styles.freeGiftHeaderText}`}
                        style={{
                            backgroundColor: bundleConfig.bundles_design?.free_gift_header_bg_color,
                            fontFamily:
                                bundleConfig.bundles_design?.bundle_widget_font_family == "Custom Store Font"
                                    ? "inherit"
                                    : bundleConfig.bundles_design?.bundle_widget_font_family,
                            color: bundleConfig?.bundles_design?.free_gift_header_color,
                            fontSize: bundleConfig?.bundles_design.free_gift_header_size,
                            fontWeight: bundleConfig?.bundles_design?.free_gift_header_style?.split(" ")[0],
                            fontStyle: bundleConfig?.bundles_design?.free_gift_header_style?.split(" ")[1],
                        }}
                    >
                        {bundleConfig?.free_gift_text}
                    </p>
                </InspectorEditableBlock>
            )}
            <div
                className={styles.freeGiftItemsWrapper}
                style={{
                    borderTop: `1px dashed ${hexToRgba(bundleConfig?.bundles_design?.product_border_color, 0.5)}`,
                }}
            >
                {_products?.map((_pd: ProductObj, index: number) => (
                    <FreeGiftItem
                        index={index}
                        _pd={_pd}
                        standAlone={standAlone}
                        bundleConfig={bundleConfig}
                        targetForm={targetForm}
                        updateProductVariants={updateProductVariants}
                        key={_pd.id}
                        currency={currency}
                        money_format={money_format}
                    />
                ))}
            </div>
        </div>
    );
};

// NOTE: Unlike the rest of this file, this default-exported component was NOT part of the
// originally-planned trim list (the plan called for dropping it entirely, since VolumeBundle.tsx
// never imports the default export directly). However, VolumeBundle.tsx does not render its own
// Add to Cart button/price footer anywhere - it relies on BundlePreviewWrapper rendering this
// default Footer internally to provide the "Add to cart" button, loading state, out-of-stock /
// unavailable messaging, and CTA text. Dropping it would silently break the Add to Cart button for
// Volume Discount bundles. It has been kept here, trimmed down to only the Volume-relevant paths
// (the order-total bar, quantity selector, skip-offer CTA, and other bundle-type branches were all
// dead for Volume and have been removed).
interface BundlePreviewFooterProps {
    standAlone: boolean;
    totalPrice: number;
    finalPrice: number;
    money_format: string;
    currency: string;
    bundleConfig: VolumeConfigInterface;
    loading: boolean;
    addToCart: () => void;
    isMobile: boolean;
}

const BundlePreviewFooter: React.FC<BundlePreviewFooterProps> = ({
    standAlone,
    totalPrice,
    finalPrice,
    money_format,
    currency,
    bundleConfig,
    loading,
    addToCart,
    isMobile,
}) => {
    const [isCtaDisabled, setIsCtaDisabled] = useState(false);
    const [isCtaHovered, setIsCtaHovered] = useState(false);

    useEffect(() => {
        const handleDisableCtaChanged = (e: Event) => {
            const customEvent = e as CustomEvent<{ disabled: boolean; availableQuantity: number }>;
            setIsCtaDisabled(customEvent.detail.disabled);
            renderBundleErrorMessage(
                customEvent.detail.disabled && customEvent.detail.availableQuantity > 1
                    ? `Oops! Just ${customEvent.detail.availableQuantity} item${
                          customEvent.detail.availableQuantity > 1 ? "s" : ""
                      } available right now.`
                    : "",
                bundleConfig?.form_id || "",
            );
        };

        window.addEventListener("disableCtaChanged", handleDisableCtaChanged);

        return () => {
            window.removeEventListener("disableCtaChanged", handleDisableCtaChanged);
        };
    }, []);

    const renderCTAContent = () => {
        const ctaText = bundleConfig?.add_to_cart_cta_text || "";
        const iconUrl = bundleConfig?.add_to_cart_cta_icon_url;
        const iconType = bundleConfig?.add_to_cart_cta_icon_type || "none";
        const iconSize = bundleConfig?.bundles_design?.add_to_cart_text_size || 16;
        const hasShortcode = /{{price}}/.test(ctaText);

        const plainText = ctaText.replace(/{{price}}/g, "").trim();

        let hasProducts = false;

        if (bundleConfig?.bundles_design?.default_cta) {
            return <>{parseCTAWithIcon(plainText, iconUrl, iconSize, iconType)}</>;
        }
        if (bundleConfig?.volume_product_selection === "all" && !standAlone) {
            hasProducts = !!window?.qb_product;
        } else {
            hasProducts = Array.isArray(bundleConfig?.products) && bundleConfig.products.length > 0;
        }

        if (!hasProducts || totalPrice <= 0) {
            return <>{parseCTAWithIcon(plainText, iconUrl, iconSize, iconType)}</>;
        }

        if (!hasShortcode) return <>{parseCTAWithIcon(ctaText, iconUrl, iconSize, iconType)}</>;

        return (
            <div className={styles.ctaContentWrapper}>
                <div className={styles.ctaTextFlex}>{parseCTAWithIcon(plainText, iconUrl, iconSize, iconType)}</div>
                <div className={styles.ctaPriceWrapper}>
                    <div>
                        {finalPrice <= 0
                            ? bundleConfig?.free_text || "Free"
                            : getPriceLabel(finalPrice, money_format, currency)}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div
            className={`${styles.footer}`}
            style={{
                borderTopLeftRadius: bundleConfig?.bundles_design?.border_radius,
                borderTopRightRadius: bundleConfig?.bundles_design?.border_radius,
                borderBottomLeftRadius: bundleConfig?.bundles_design?.border_radius,
                borderBottomRightRadius: bundleConfig?.bundles_design?.border_radius,
            }}
        >
            <div
                style={{
                    flexDirection: isMobile ? "column-reverse" : "row",
                    gap: isMobile ? 0 : "10px",
                }}
                className={`${styles.addToCartWrapper} ${styles.addToCartRow}`}
            >
                {(standAlone ? true : !bundleConfig?.bundles_design?.default_cta) && (
                    <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="addToCartCta">
                        <div className={styles.buttonWrapper}>
                            <Button
                                title={renderCTAContent()}
                                onClick={standAlone ? addToCart : () => {}}
                                onMouseEnter={() => setIsCtaHovered(true)}
                                onMouseLeave={() => setIsCtaHovered(false)}
                                loading={loading}
                                className={
                                    bundleConfig?.bundles_design?.default_cta && window.kefi_bundles.addToCartClasses
                                        ? `${window.kefi_bundles.addToCartClasses} ${styles.kefiAddToCart}`
                                        : ""
                                }
                                id="addToCartButton"
                                disabledCta={isCtaDisabled && standAlone}
                                inheritLoadingColor={true}
                                style={{
                                    flex: 1,
                                    ...(bundleConfig?.bundles_design?.default_cta &&
                                    window.kefi_bundles.addToCartClasses
                                        ? {
                                              fontFamily: "var(--font-body-family)",
                                              position: "relative",
                                              top: "unset",
                                              left: "unset",
                                              bottom: "unset",
                                              right: "unset",
                                              margin: "1px 0",
                                              float: "unset",
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                          }
                                        : {
                                              background:
                                                  (isCtaHovered &&
                                                      bundleConfig?.bundles_design?.add_to_cart_hover_bg_color) ||
                                                  bundleConfig?.bundles_design?.add_to_cart_bg_color,
                                              transition: "background 0.15s ease",
                                              borderRadius: bundleConfig?.bundles_design?.add_to_cart_border_radius,
                                              color: bundleConfig?.bundles_design?.add_to_cart_text_color,
                                              fontSize:
                                                  bundleConfig?.bundles_design?.add_to_cart_text_size < 50
                                                      ? bundleConfig?.bundles_design?.add_to_cart_text_size
                                                      : 50,
                                              fontStyle:
                                                  bundleConfig?.bundles_design?.add_to_cart_text_style?.split(" ")[1],
                                              fontWeight:
                                                  bundleConfig?.bundles_design?.add_to_cart_text_style?.split(" ")[0],
                                              padding: "16px 24px",
                                              border: "none",
                                              borderWidth: bundleConfig?.bundles_design?.add_to_cart_border_width || 0,
                                              borderStyle: "solid",
                                              borderColor:
                                                  bundleConfig?.bundles_design?.add_to_cart_border_color || undefined,
                                          }),
                                    cursor: "pointer",
                                    minWidth: "100%",
                                    width: "100%",
                                    height: "auto",
                                }}
                            />
                        </div>
                    </InspectorEditableBlock>
                )}
            </div>
        </div>
    );
};

export default BundlePreviewFooter;
